import { useQuery } from '@tanstack/react-query';
import type { AgentTimePage, BillingSummary, SuperadminBillingView } from '@chatsdet/types';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

async function fetchJson<T>(path: string, fallback: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include' });
  if (!res.ok) throw await toApiError(res, fallback);
  return res.json();
}

/** `GET /usage/billing`. Refused with `BILLING_UNAVAILABLE` unless `user.canViewBilling`. */
export function useBillingSummary(options: { enabled: boolean }) {
  return useQuery({
    queryKey: ['billing-summary'],
    queryFn: async () =>
      (await fetchJson<{ ok: boolean; data: BillingSummary }>('/usage/billing', 'Failed to load billing'))
        .data,
    enabled: options.enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/** `GET /usage/billing/agent-time`: what used the agent hours in `[start, end)`. */
export function useAgentTime(start: string, end: string, page: number, enabled: boolean) {
  const qs = new URLSearchParams({ start, end, page: String(page) });
  return useQuery({
    queryKey: ['billing-agent-time', start, end, page],
    queryFn: () =>
      fetchJson<{ ok: boolean } & AgentTimePage>(
        `/usage/billing/agent-time?${qs}`,
        'Failed to load agent time'
      ),
    enabled,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
    retry: false,
  });
}

/** `GET /superadmin/billing/:orgId`. */
export function useSuperadminBilling(orgId: string | null) {
  return useQuery({
    queryKey: ['superadmin-billing', orgId],
    queryFn: async () =>
      (
        await fetchJson<{ ok: boolean; data: SuperadminBillingView }>(
          `/superadmin/billing/${orgId}`,
          'Failed to load billing'
        )
      ).data,
    enabled: Boolean(orgId),
    retry: false,
  });
}
